/**
 * Reviews client — public listing, customer review submission.
 *
 * Public listing (PDP) does NOT need auth. Customer submission DOES need
 * a JWT, minted via App Proxy session (same path as the loyalty client).
 */

const API_BASE = 'https://subs-api.highereducation.shop/v1';
const APP_PROXY_SESSION = '/apps/subscriptions/session';

export class ReviewsClient {
  /** @type {string | null} */
  #token = null;
  /** @type {number | null} */
  #tokenExpiresAt = null;

  // -----------------------------------------------------------------
  // Public — no auth required
  // -----------------------------------------------------------------

  /** @returns {Promise<ProductReviewsResponse>} */
  async listForProduct(productId, { page = 1, pageSize = 10, sort = 'newest' } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      sort,
    });
    const resp = await fetch(
      `${API_BASE}/products/${encodeURIComponent(productId)}/reviews?${params.toString()}`,
      { method: 'GET', headers: { Accept: 'application/json' } },
    );
    if (!resp.ok) {
      throw { code: 'http_error', status: resp.status };
    }
    return resp.json();
  }

  // -----------------------------------------------------------------
  // Authenticated
  // -----------------------------------------------------------------

  async #ensureToken() {
    const now = Math.floor(Date.now() / 1000);
    if (this.#token && this.#tokenExpiresAt && this.#tokenExpiresAt - now > 60) {
      return this.#token;
    }
    let resp;
    try {
      resp = await fetch(APP_PROXY_SESSION, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
    } catch (err) {
      throw { code: 'network', cause: err };
    }
    if (resp.status === 401) {
      this.#token = null;
      this.#tokenExpiresAt = null;
      throw { code: 'not_logged_in' };
    }
    if (!resp.ok) throw { code: 'proxy_error', status: resp.status };
    const body = await resp.json();
    if (!body.token) throw { code: 'proxy_error', reason: 'no_token' };
    this.#token = body.token;
    this.#tokenExpiresAt = now + (body.expiresIn ?? 3600);
    return this.#token;
  }

  async #callAuthed(path, init = {}) {
    const token = await this.#ensureToken();
    const resp = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (resp.status === 401) {
      this.#token = null;
      this.#tokenExpiresAt = null;
      const retryToken = await this.#ensureToken();
      const retry = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...(init.headers ?? {}),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${retryToken}`,
        },
      });
      return this.#handleResponse(retry);
    }
    return this.#handleResponse(resp);
  }

  async #handleResponse(resp) {
    const ct = resp.headers.get('content-type') ?? '';
    const body = ct.includes('application/json') ? await resp.json() : null;
    if (!resp.ok) {
      throw { code: body?.error ?? 'http_error', status: resp.status, body };
    }
    return body;
  }

  /** @returns {Promise<{ reviews: MyReview[] }>} */
  listMine() {
    return this.#callAuthed('/me/reviews', { method: 'GET' });
  }

  /**
   * @param {Object} input
   * @param {string|number} input.product_id
   * @param {string|number} input.order_id
   * @param {number} input.rating
   * @param {string|null} [input.title]
   * @param {string} input.body
   * @param {string} input.customer_name
   * @returns {Promise<{ review_id: string, status: string, points_awarded: number }>}
   */
  submit(input) {
    return this.#callAuthed('/me/reviews', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
}

export function describeReviewError(err) {
  const e = /** @type {any} */ (err);
  switch (e?.code) {
    case 'not_logged_in':
      return 'Please sign in to leave a review.';
    case 'network':
      return 'Network problem. Please try again.';
    case 'rating_invalid':
      return 'Pick a rating from 1 to 5 stars.';
    case 'body_too_short':
      return 'Tell us a little more (at least 10 characters).';
    case 'body_too_long':
      return 'Please keep your review under 2000 characters.';
    case 'title_too_long':
      return 'Title is too long.';
    case 'name_invalid':
      return 'A display name is required.';
    case 'duplicate':
      return 'You\'ve already reviewed this product for that order.';
    case 'not_verified':
      return 'Reviews are limited to verified buyers. Please use the link from your order confirmation or review-request email.';
    case 'order_not_found':
      return 'We couldn\'t find that order.';
    case 'product_not_in_order':
      return 'That product wasn\'t part of the order you cited.';
    case 'no_email_in_session':
      return 'We couldn\'t read your account email. Try signing out and back in.';
    case 'submit_failed':
      return 'Submission failed. Please try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/**
 * @typedef {Object} ReviewItem
 * @property {string} id
 * @property {string} name
 * @property {number} rating
 * @property {string|null} title
 * @property {string} body
 * @property {boolean} verified
 * @property {string} created_at
 *
 * @typedef {Object} ProductReviewsResponse
 * @property {{ avgRating: number|null, publishedCount: number, ratingDistribution: Record<string, number>|null }} aggregate
 * @property {ReviewItem[]} reviews
 * @property {{ page: number, pageSize: number, totalPages: number }} pagination
 *
 * @typedef {Object} MyReview
 * @property {string} id
 * @property {string} product_id
 * @property {string|null} product_title
 * @property {string|null} order_id
 * @property {number} rating
 * @property {string|null} title
 * @property {string} body
 * @property {string} status
 * @property {boolean} verified_buyer
 * @property {string|null} rejection_reason
 * @property {string} created_at
 * @property {string|null} published_at
 */
